import React, { useEffect, useState } from 'react';

// components
import Header from '../../../components/Header/Index';
import Footer from '../../../components/Footer';
import SignUpModel from '../../SignUpModel';
import RolloverPopup from '../../RolloverPopup';
import LayoutBanner from './LayoutBanner';

// css
import '../../../themes/layout.scss';

// mui utils
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';

// iAngels utils
import LoadingIndicator from '../../../components/Generic/LoadingIndicator';
import RoutGuard from '../../../auth/RouteGuardUniversal';
import { track, pageView } from '../../../services/analytics';

import {
  getNeedsDetailsSelector,
  getRolloverPopupSelector,
  getNeedsAccreditationSelector,
  getTraitsSelector,
  getUserSelector,
  getNeedsVerifySelector,
  getAuthAttemptingSelector,
  getIsPreviewerSelector,
  getIsAdminSelector,
  getIsGuestSelector,
  getIsIncompleteKycSelector
} from '../../App/selectors';
import { getIsImpersonatedSelector } from '../../Impersonate/selectors';

// third party lib
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { titleSlug } from '../../../helpers/iangels';

const MainLayout = (props) => {
  // hooks
  const params = useParams();

  // selectors
  const needsAccreditation = useSelector(getNeedsAccreditationSelector);
  const authLoading = useSelector(getAuthAttemptingSelector);
  const needDetails = useSelector(getNeedsDetailsSelector);
  const rolloverPopup = useSelector(getRolloverPopupSelector);
  const traits = useSelector(getTraitsSelector);
  const user = useSelector(getUserSelector);

  // banner selectors
  const isImpersonated = useSelector(getIsImpersonatedSelector);
  const isPreviewer = useSelector(getIsPreviewerSelector);
  const isVerified = useSelector(getNeedsVerifySelector);
  const isAdmin = useSelector(getIsAdminSelector);
  const isGuest = useSelector(getIsGuestSelector);
  const isIncompleteKyc = useSelector(getIsIncompleteKycSelector);

  // state
  const [showModeBanner, setShowModeBanner] = useState(false);
  const [showFooter, setShowFooter] = useState(false);

  useEffect(() => {
    setShowModeBanner(isAdmin || isPreviewer || isImpersonated || isGuest || isIncompleteKyc);
  }, [isAdmin, isPreviewer, isImpersonated, isGuest, isIncompleteKyc]);

  useEffect(() => {
    setShowFooter(!props.lockGuard || user != null);
  }, [props.lockGuard, user]);

  useEffect(() => {
    pageView(props.track, window.location.href, { routerPath: props.navigateTo });

    if (isVerified === false) track('Resend Verification Email');
  });

  // Use traits (backend User model) as source of truth; JWT app_metadata no longer has has_details after scope reduction
  const userNeedDetails = () => {
    if (!user) return false;
    if (!user?.app_metadata) return true;
    const hasDetails = user?.app_metadata?.has_details ?? user?.['https://iangels.com/has_details'];
    return !hasDetails;
  };

  const userLevel = () => {
    const level = traits?.level ?? user?.app_metadata?.level ?? user?.level;
    return !!(needsAccreditation && level === 'unknown');
  };

  return (
    <div style={{ width: '100%', height: 'inherit' }}>
      <div className="app" style={{ height: 'inherit', justifyContent: 'space-between' }}>
        <div>
          {((needDetails && userNeedDetails()) || userLevel()) && <SignUpModel />}
          {rolloverPopup && <RolloverPopup/>  }
          <Header
            showModeBanner={showModeBanner}
            setShowModeBanner={setShowModeBanner}
            isAdmin={isAdmin}
            isPreviewer={isPreviewer}
            isImpersonated={isImpersonated}
            isGuest={isGuest}
            isIncompleteKyc={isIncompleteKyc}
          />
          {isVerified === false ? <LayoutBanner email={user?.email} /> : null}

          <Helmet>
            {props.title ? (
              <title>{`${props.title} - iAngels`}</title>
            ) : params?.slug ? (
              <title>{`${titleSlug(params?.slug)} - iAngels`} </title>
            ) : (
              <title>{'iAngels'}</title>
            )}
          </Helmet>
          <RoutGuard
            lockGuard={props.lockGuard}
            navigateTo={params?.slug ? `${props.navigateTo}/${params?.slug}` : props.navigateTo}
            hasNext={props.hasNext}
            isSignup={props.isSignup}
          />
          <main
            role="main"
            className="main"
            style={{
              marginTop: isVerified === false && showModeBanner ? 130 : isVerified === false || showModeBanner ? 65 : 0,
            }}
          >
            {authLoading ? (
              <LoadingIndicator />
            ) : (
              <Box>
                <Container style={{ maxWidth: 1200, minHeight: 570 }}>
                  {React.Children.toArray(props.children)}
                </Container>
              </Box>
            )}
          </main>
        </div>
        {showFooter && (
          <footer className="footer">
            <Footer />
          </footer>
        )}
      </div>
    </div>
  );
};

export default MainLayout;
